import { useEffect, useRef, useState, type RefObject } from 'react';
import MagnifyingGlass from '@/assets/icons/magnifying_glass.svg?react';
import CloseX from '@/assets/icons/close-x.svg?react';
import type { PlayerForSaveDto } from '@/api/models';
import { PlayerDB } from '@/db/repositories';
import { searchPlayersByName } from '@/library/PlayerSearchHelper';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useDebouncedInput } from '@/hooks/useDebouncedInput';
import styles from './PlayerAutosuggest.module.scss';

export type PlayerSuggestion = Pick<PlayerForSaveDto, 'playerId' | 'fullName'>;

interface Props {
  inputRef?: RefObject<HTMLInputElement | null>;
  wrapperRef?: RefObject<HTMLDivElement | null>;
  players?: PlayerForSaveDto[];
  defaultPlayer?: PlayerSuggestion;
  label?: string;
  variant?: 'filter' | 'field';
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelect: (player: PlayerSuggestion | undefined) => void;
  // eslint-disable-next-line no-unused-vars
  onSearch?: (text: string | undefined) => void;
}

export function PlayerAutosuggest({ inputRef, wrapperRef, players, defaultPlayer, label = 'Search by player', variant = 'filter', disabled, onSelect, onSearch }: Props) {
  const localInputRef = useRef<HTMLInputElement>(null);
  const localWrapperRef = useRef<HTMLDivElement>(null);
  const activeInputRef = inputRef ?? localInputRef;
  const activeWrapperRef = wrapperRef ?? localWrapperRef;
  const wrapperClass = `${styles.wrapper} ${variant === 'field' ? styles.rectangle : styles.filter}`;
  const [searchOpen, setSearchOpen] = useState(false);
  const [text, setText] = useState(defaultPlayer?.fullName ?? '');
  const [suggestions, setSuggestions] = useState<PlayerSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSuggestion | undefined>(defaultPlayer);
  const localPlayers = useRef<Promise<PlayerForSaveDto[]> | undefined>(undefined);
  const playerRequestSequence = useRef(0);
  const selectedPlayerText = useRef<string | undefined>(defaultPlayer?.fullName);
  const debouncedText = useDebouncedInput(text, 300);
  const showEmpty = variant === 'field' && searchOpen && Boolean(text.trim()) && text === debouncedText && !isLoadingSuggestions && suggestions.length === 0;
  const showSuggestions = suggestions.length > 0 || showEmpty;

  const handleTextChange = (value: string) => {
    setText(value);
    setSearchOpen(true);

    playerRequestSequence.current += 1;
    selectedPlayerText.current = undefined;
    setSuggestions([]);
    setIsLoadingSuggestions(false);
    if (!selectedPlayer) return;

    setSelectedPlayer(undefined);
    onSelect(undefined);
  };

  const handleClear = () => {
    playerRequestSequence.current += 1;
    setText('');
    setSearchOpen(false);
    setSuggestions([]);
    setIsLoadingSuggestions(false);
    setSelectedPlayer(undefined);
    selectedPlayerText.current = undefined;
    onSelect(undefined);
    if (variant === 'filter') activeInputRef.current?.focus();
  };

  const handlePlayerSelect = (player: PlayerSuggestion) => {
    playerRequestSequence.current += 1;
    setText(player.fullName);
    setSearchOpen(false);
    setSelectedPlayer(player);
    selectedPlayerText.current = player.fullName;
    setSuggestions([]);
    setIsLoadingSuggestions(false);
    onSelect(player);
  };

  const handlePlayerClickOutside = () => {
    setSearchOpen(false);
    playerRequestSequence.current += 1;
    setSuggestions([]);
    setIsLoadingSuggestions(false);
  };

  useClickOutside(activeWrapperRef, 1, handlePlayerClickOutside);

  useEffect(() => {
    const loadLocalPlayers = async () => {
      try {
        const records = players ?? (await PlayerDB.list());
        return records;
      } catch (error) {
        console.error('Unable to load local players:', error);
        return [];
      }
    };

    localPlayers.current = loadLocalPlayers();
  }, [players]);

  useEffect(() => {
    const trimmedText = debouncedText.trim();
    const shouldSearch = trimmedText !== '' && trimmedText !== selectedPlayerText.current;
    const requestSequence = ++playerRequestSequence.current;

    setSuggestions([]);
    setIsLoadingSuggestions(shouldSearch);
    if (!shouldSearch) return;

    const loadPlayers = async () => {
      let players: PlayerSuggestion[] = [];
      try {
        const cachedPlayers = await localPlayers.current;
        players = searchPlayersByName(cachedPlayers ?? [], debouncedText);
      } catch (error) {
        console.error('Unable to load player suggestions:', error);
      }

      if (requestSequence !== playerRequestSequence.current) return;
      setSuggestions(players);
      setIsLoadingSuggestions(false);
    };

    loadPlayers();

    return () => {
      playerRequestSequence.current += 1;
    };
  }, [debouncedText]);

  useEffect(() => {
    if (selectedPlayer || text !== debouncedText) return;
    onSearch?.(debouncedText.trim() || undefined);
  }, [debouncedText, text, selectedPlayer, onSearch]);

  return (
    <div ref={localWrapperRef} className={wrapperClass}>
      <fieldset className={styles.field}>
        <legend className={styles.legend}>
          {variant === 'field' && label}
          {variant === 'filter' && <>&nbsp;</>}
        </legend>
        <div className={styles.control}>
          <MagnifyingGlass className={styles.magnifyingGlass} />
          <input
            ref={activeInputRef}
            type="text"
            title={label}
            disabled={disabled}
            autoComplete="off"
            className={styles.filterInput}
            onChange={(event) => handleTextChange(event.target.value)}
            value={text}
          />
          {text !== '' && variant === 'filter' && (
            <button type="button" className={styles.clearButton} title="Clear search" onClick={handleClear}>
              <CloseX className={styles.closeX} />
            </button>
          )}
        </div>
      </fieldset>
      {isLoadingSuggestions && <div className={styles.suggestionStatus}>Searching…</div>}
      {showSuggestions && (
        <ul className={styles.suggestions} role="listbox" title="Player suggestions">
          {showEmpty && <li className={styles.empty}>No players found.</li>}
          {suggestions.map((player) => (
            <li key={player.playerId} role="option">
              <button type="button" onClick={() => handlePlayerSelect(player)}>
                {player.fullName}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
